import { memo, useCallback, useId } from 'react';
import { useTranslation } from '@aimsos/i18n';
import { Input } from '@aimsos/ui';

import ExistingRecordNotice from './ExistingRecordNotice.jsx';
import RecordTypeChoice from './RecordTypeChoice.jsx';
import { fieldErrorText } from './fieldErrors.js';
import { CREATABLE_TYPES } from '../../constants/createRecordFields.js';

/** Step 1: what is being created and the field that tells it apart from the rest. */
function IdentityStep({ entityType, values, errors, exists, domainRoot, onSelectType, onChange, onTouch }) {
  const { t } = useTranslation('unified-profiles');
  const typeLegendId = useId();

  const bind = useCallback(
    (name) => ({
      value: values[name] ?? '',
      error: fieldErrorText(t, name, errors[name]),
      onChange: (event) => onChange(name, event.target.value),
      onBlur: () => onTouch(name),
    }),
    [values, errors, onChange, onTouch, t],
  );

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-2">
        <div>
          <p id={typeLegendId} className="text-sm font-semibold text-content">
            {t('create.type.legend')}
          </p>
          <p className="text-xs text-content-muted">{t('create.type.help')}</p>
        </div>
        <RecordTypeChoice
          types={CREATABLE_TYPES}
          selectedId={entityType}
          onSelect={onSelectType}
          labelledBy={typeLegendId}
        />
      </div>

      {/* One field under the other, full width, as in the prototype. */}
      {entityType === 'customers' ? (
        <div className="flex flex-col gap-4">
          <Input
            label={t('create.fields.fullName.label')}
            placeholder={t('create.fields.fullName.placeholder')}
            autoComplete="name"
            {...bind('fullName')}
          />
          <Input
            label={t('create.fields.email.label')}
            placeholder={t('create.fields.email.placeholder')}
            type="email"
            autoComplete="email"
            {...bind('email')}
          />
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          <Input
            label={t('create.fields.name.label')}
            placeholder={t('create.fields.name.placeholder')}
            autoComplete="organization"
            {...bind('name')}
          />
          <Input
            label={t('create.fields.domain.label')}
            placeholder={t('create.fields.domain.placeholder')}
            helperText={t('create.fields.domain.helper')}
            autoComplete="url"
            {...bind('domain')}
          />
        </div>
      )}

      <ExistingRecordNotice
        entityType={entityType}
        matches={exists.matches}
        checking={exists.checking}
        domainRoot={domainRoot}
      />
    </div>
  );
}

export default memo(IdentityStep);
